import { Injectable, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { GeoLocation, Label } from '@wilson/interfaces';
import {
  getAgreementIdFromActivities,
  getBreakOptionFromActivities,
  isSimpleShiftActivities,
  SimplifiedShiftBreakDurationMinutes,
  SimplifiedShiftBreakOption,
  sortActivitiesForSimpleShift,
  SimpleShiftActivity,
} from './helpers/is-simple-shift-activities';
import { SimplifiedDrawerShift } from './types';

export type { SimplifiedShiftBreakOption, SimplifiedShiftBreakDurationMinutes };

export type SimplifiedActivitiesViewMode = 'simple' | 'extended';

export const SIMPLIFIED_SHIFT_TIER_TWO_FORM_FIELDS = [
  'comment',
  'planningComment',
  'maintenanceNumber',
  'sharePlanNumber',
] as const;

export type SimplifiedShiftTierTwoSection =
  | (typeof SIMPLIFIED_SHIFT_TIER_TWO_FORM_FIELDS)[number]
  | 'attachments'
  | 'grouping'
  | 'shiftFeatures';

export interface SimplifiedShiftDrawerFormValue {
  name: string;
  userId: string | null;
  shiftDate: Date | null;
  startTime: Date | null;
  endTime: Date | null;
  activityCategoryId: string | null;
  breakOption: SimplifiedShiftBreakOption;
  breakStartTime: Date | null;
  breakEndTime: Date | null;
  location: GeoLocation | null;
  projectId: string | null;
  agreementId: string | null;
  labels: Label[];
  shiftCategoryId: string | null;
  organizationalUnitId: string | null;
  comment: string;
  planningComment: string;
  maintenanceNumber: string;
  sharePlanNumber: string;
}

export type SimplifiedShiftDrawerFormControls = {
  [Key in keyof SimplifiedShiftDrawerFormValue]: FormControl<
    SimplifiedShiftDrawerFormValue[Key]
  >;
};

export type SimplifiedShiftDrawerForm =
  FormGroup<SimplifiedShiftDrawerFormControls>;

/** Free-text fields that can be mid-edit when an autosave reloads the shift. */
const PRESERVABLE_FREE_TEXT_FIELDS = [
  'name',
  'comment',
  'planningComment',
  'maintenanceNumber',
  'sharePlanNumber',
] as const satisfies readonly (keyof SimplifiedShiftDrawerFormValue)[];

export type SimplifiedShiftDrawerFormInitializeOptions = {
  preserveTierTwoPromotion?: boolean;
};

const PATCH_OPTIONS = {
  onlySelf: true,
  emitEvent: false,
  emitViewToModelChange: false,
} as const;

@Injectable()
export class SimplifiedShiftDrawerFormService {
  readonly form: SimplifiedShiftDrawerForm = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    userId: new FormControl<string | null>(null),
    shiftDate: new FormControl<Date | null>(null, Validators.required),
    startTime: new FormControl<Date | null>(null, Validators.required),
    endTime: new FormControl<Date | null>(null, Validators.required),
    activityCategoryId: new FormControl<string | null>(
      null,
      Validators.required,
    ),
    breakOption: new FormControl<SimplifiedShiftBreakOption>(null),
    breakStartTime: new FormControl<Date | null>(null),
    breakEndTime: new FormControl<Date | null>(null),
    location: new FormControl<GeoLocation | null>(null, Validators.required),
    projectId: new FormControl<string | null>(null),
    agreementId: new FormControl<string | null>(null),
    labels: new FormControl<Label[]>([], { nonNullable: true }),
    shiftCategoryId: new FormControl<string | null>(null, Validators.required),
    organizationalUnitId: new FormControl<string | null>(
      null,
      Validators.required,
    ),
    comment: new FormControl('', { nonNullable: true }),
    planningComment: new FormControl('', {
      nonNullable: true,
      validators: [Validators.maxLength(450)],
    }),
    maintenanceNumber: new FormControl('', { nonNullable: true }),
    sharePlanNumber: new FormControl('', { nonNullable: true }),
  });

  readonly promotedTierTwoFields = signal<
    readonly SimplifiedShiftTierTwoSection[]
  >([]);
  readonly activitiesViewMode = signal<SimplifiedActivitiesViewMode>('simple');
  readonly hasMixedAgreements = signal(false);
  readonly canReturnToSimpleView = signal(false);

  private tierTwoPromotionLocked = false;
  private userForcedExtendedMode = false;
  private focusedField: keyof SimplifiedShiftDrawerFormValue | null = null;
  private lastInitializedValues: SimplifiedShiftDrawerFormValue | null = null;

  setFocusedField(field: keyof SimplifiedShiftDrawerFormValue | null): void {
    this.focusedField = field;
  }

  initialize(
    shift: SimplifiedDrawerShift,
    options?: SimplifiedShiftDrawerFormInitializeOptions,
  ): void {
    const { nextValues, agreementValues, isSimple } =
      this.buildFormStateFromShift(shift);

    this.lastInitializedValues = { ...nextValues };
    this.form.reset(nextValues, { emitEvent: false });
    this.hasMixedAgreements.set(agreementValues.hasMixedAgreements);
    this.syncActivitiesViewMode(isSimple, options?.preserveTierTwoPromotion);
    this.syncTierTwoPromotionFromForm(options?.preserveTierTwoPromotion);
  }

  /**
   * Syncs the form from a reloaded shift after autosave without a full reset.
   * Matches the detail drawer's patchForm(): selective patchValue with
   * emitViewToModelChange disabled so ngx-editor CVAs do not re-dirty controls.
   */
  patchFromShift(
    shift: SimplifiedDrawerShift,
    options?: SimplifiedShiftDrawerFormInitializeOptions,
  ): void {
    const { nextValues, agreementValues, isSimple } =
      this.buildFormStateFromShift(shift, { forPostSaveSync: true });

    for (const field of Object.keys(
      nextValues,
    ) as (keyof SimplifiedShiftDrawerFormValue)[]) {
      const serverValue = nextValues[field];
      const control = this.form.controls[field];

      if (this.focusedField === field) {
        // Never overwrite a control the user is actively editing (e.g. mid
        // keystroke with a trailing space) - only drop its dirty flag once
        // the content already matches the server, ignoring insignificant
        // whitespace/tag differences.
        if (this.areFormValuesEqual(control.value, serverValue)) {
          control.markAsPristine();
        }
      } else if (!this.shouldPreserveFieldOnPatch(field, serverValue)) {
        control.patchValue(serverValue as never, PATCH_OPTIONS);
        control.markAsPristine();
      }
    }

    this.lastInitializedValues = {
      ...nextValues,
    };
    this.hasMixedAgreements.set(agreementValues.hasMixedAgreements);
    this.syncActivitiesViewMode(
      isSimple,
      options?.preserveTierTwoPromotion ?? true,
    );
  }

  /**
   * Clears dirty flags where the current value still matches the last init.
   * Returns true when the form is pristine afterwards.
   */
  clearSpuriousDirtyMatchingLastInit(): boolean {
    if (!this.lastInitializedValues) {
      return this.form.pristine;
    }

    for (const field of Object.keys(
      this.lastInitializedValues,
    ) as (keyof SimplifiedShiftDrawerFormValue)[]) {
      const control = this.form.controls[field];
      // Never spuriously clear dirty on the field the user is actively
      // editing - only patchFromShift's stricter, server-value comparison
      // should do that.
      const isFocused = field === this.focusedField;
      if (
        !isFocused &&
        control.dirty &&
        this.areFormValuesEqual(
          control.value,
          this.lastInitializedValues[field],
        )
      ) {
        control.markAsPristine();
      }
    }

    return this.form.pristine;
  }

  private buildFormStateFromShift(
    shift: SimplifiedDrawerShift,
    options?: { forPostSaveSync?: boolean },
  ): {
    nextValues: SimplifiedShiftDrawerFormValue;
    agreementValues: ReturnType<typeof getAgreementIdFromActivities>;
    isSimple: boolean;
  } {
    const sortedActivities = sortActivitiesForSimpleShift(shift.activities);
    const agreementValues = getAgreementIdFromActivities(sortedActivities);
    const nextValues = this.buildFormValuesFromShift(
      shift,
      sortedActivities,
      agreementValues.agreementId,
    );

    if (options?.forPostSaveSync) {
      this.preserveExactBreakMode(nextValues);
    }

    return {
      nextValues,
      agreementValues,
      isSimple: isSimpleShiftActivities(sortedActivities),
    };
  }

  private shouldPreserveFieldOnPatch(
    field: keyof SimplifiedShiftDrawerFormValue,
    serverValue: SimplifiedShiftDrawerFormValue[keyof SimplifiedShiftDrawerFormValue],
  ): boolean {
    const control = this.form.controls[field];
    const valuesDiffer = !this.areFormValuesEqual(control.value, serverValue);
    if (!valuesDiffer) {
      return false;
    }

    return this.isPreservableFreeTextField(field) && control.dirty;
  }

  /**
   * Exact break with a 30/45/60-minute window is stored the same as the duration
   * pills. Keep the exact UI across autosave syncs so it does not snap back.
   */
  private preserveExactBreakMode(
    nextValues: SimplifiedShiftDrawerFormValue,
  ): void {
    if (this.form.controls.breakOption.value !== 'exact') {
      return;
    }

    nextValues.breakOption = 'exact';
    nextValues.breakStartTime = this.form.controls.breakStartTime.value;
    nextValues.breakEndTime = this.form.controls.breakEndTime.value;
  }

  private buildFormValuesFromShift(
    shift: SimplifiedDrawerShift,
    sortedActivities: SimpleShiftActivity[],
    agreementId: string | null,
  ): SimplifiedShiftDrawerFormValue {
    const firstActivity = sortedActivities[0];
    const lastActivity = sortedActivities[sortedActivities.length - 1];
    const breakValues = getBreakOptionFromActivities(sortedActivities);

    return {
      name: shift.name,
      userId: shift.userId,
      shiftDate: this.toDate(shift.startDate),
      startTime: this.toDate(
        firstActivity?.startDatetime ?? shift.startDatetime,
      ),
      endTime: this.toDate(lastActivity?.endDatetime ?? shift.endDatetime),
      activityCategoryId: firstActivity?.activityCategoryId ?? null,
      breakOption: breakValues.breakOption,
      breakStartTime: breakValues.breakStartTime,
      breakEndTime: breakValues.breakEndTime,
      location: firstActivity?.startLocation ?? null,
      projectId: shift.projectId ?? null,
      agreementId,
      labels: shift.labels ?? [],
      shiftCategoryId: shift.shiftCategoryId,
      organizationalUnitId: shift.organizationalUnitId,
      comment: shift.comment ?? '',
      planningComment: shift.planningComment ?? '',
      maintenanceNumber: shift.maintenanceNumber ?? '',
      sharePlanNumber: shift.sharePlanNumber ?? '',
    };
  }

  private syncTierTwoPromotionFromForm(
    preserveTierTwoPromotion?: boolean,
  ): void {
    if (preserveTierTwoPromotion) {
      return;
    }

    this.tierTwoPromotionLocked = false;
    const initialValue = this.form.getRawValue();
    this.promotedTierTwoFields.set(
      SIMPLIFIED_SHIFT_TIER_TWO_FORM_FIELDS.filter((field) =>
        this.hasValue(initialValue[field]),
      ),
    );
  }

  private isPreservableFreeTextField(
    field: keyof SimplifiedShiftDrawerFormValue,
  ): boolean {
    return (PRESERVABLE_FREE_TEXT_FIELDS as readonly string[]).includes(field);
  }

  private areFormValuesEqual(left: unknown, right: unknown): boolean {
    if (left instanceof Date && right instanceof Date) {
      return left.getTime() === right.getTime();
    }

    if (typeof left === 'string' || typeof right === 'string') {
      return (
        this.normalizeComparableString(left) ===
        this.normalizeComparableString(right)
      );
    }

    return left === right;
  }

  /** Treat empty editor HTML (`<p></p>`) like '' and ignore tag/whitespace noise. */
  private normalizeComparableString(value: unknown): string {
    return String(value ?? '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  syncActivitiesViewMode(
    isSimple: boolean,
    preserveUserPreference = false,
  ): void {
    this.canReturnToSimpleView.set(isSimple);

    if (!isSimple) {
      this.userForcedExtendedMode = false;
      this.activitiesViewMode.set('extended');
      return;
    }

    if (!preserveUserPreference) {
      this.userForcedExtendedMode = false;
      this.activitiesViewMode.set('simple');
      return;
    }

    if (!this.userForcedExtendedMode) {
      this.activitiesViewMode.set('simple');
    }
  }

  switchToExtendedActivitiesView(): void {
    this.userForcedExtendedMode = true;
    this.activitiesViewMode.set('extended');
  }

  switchToSimpleActivitiesView(): void {
    if (!this.canReturnToSimpleView()) {
      return;
    }
    this.userForcedExtendedMode = false;
    this.activitiesViewMode.set('simple');
  }

  clearMixedAgreements(): void {
    this.hasMixedAgreements.set(false);
  }

  promoteTierTwoSection(section: SimplifiedShiftTierTwoSection): void {
    if (this.tierTwoPromotionLocked) {
      return;
    }

    this.promotedTierTwoFields.update((fields) =>
      fields.includes(section) ? fields : [...fields, section],
    );
  }

  lockTierTwoPromotion(): void {
    this.tierTwoPromotionLocked = true;
  }

  resetTierTwoPromotion(): void {
    this.tierTwoPromotionLocked = false;
    this.userForcedExtendedMode = false;
    this.promotedTierTwoFields.set([]);
    this.hasMixedAgreements.set(false);
    this.canReturnToSimpleView.set(false);
  }

  isTierTwoFieldPromoted(section: SimplifiedShiftTierTwoSection): boolean {
    return this.promotedTierTwoFields().includes(section);
  }

  private hasValue(value: string): boolean {
    return value.replace(/<[^>]*>/g, '').trim().length > 0;
  }

  private toDate(value: string | null | undefined): Date | null {
    if (!value) {
      return null;
    }

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
}
